<template>
  <v-input
    hide-details="auto"
    :rules="editor && required && editor.isEmpty ? rule.required : []"
    :style="disabled ? 'opacity: 0.5' : 'opacity: 1'"
  >
    <div class="w-100 border-all rounded">
      <label
        v-if="label"
        class="d-block body-2 text--secondary pa-3 gray lighten-4 border-bottom"
        >{{ label }}</label
      >
      <div class="border-bottom pa-1" v-if="editor">
        <v-btn
          @click="editor.chain().focus().toggleBold().run()"
          :color="editor.isActive('bold') ? 'primary' : 'gray'"
          icon
          :disabled="!editor.can().chain().focus().toggleBold().run()"
        >
          <v-icon>mdi-format-bold</v-icon>
        </v-btn>

        <v-btn
          @click="editor.chain().focus().toggleItalic().run()"
          :color="editor.isActive('italic') ? 'primary' : 'gray'"
          icon
          :disabled="!editor.can().chain().focus().toggleItalic().run()"
        >
          <v-icon>mdi-format-italic</v-icon>
        </v-btn>

        <v-btn
          @click="editor.chain().focus().toggleStrike().run()"
          :color="editor.isActive('strike') ? 'primary' : 'gray'"
          icon
          :disabled="!editor.can().chain().focus().toggleStrike().run()"
        >
          <v-icon>mdi-format-strikethrough</v-icon>
        </v-btn>

        <v-btn @click="editor.chain().focus().unsetAllMarks().run()" icon>
          <v-icon>mdi-format-clear</v-icon>
        </v-btn>

        <v-btn
          @click="editor.chain().focus().setParagraph().run()"
          :color="editor.isActive('paragraph') ? 'primary' : 'gray'"
          icon
        >
          <v-icon>mdi-format-paragraph</v-icon>
        </v-btn>

        <v-btn
          @click="editor.chain().focus().toggleHeading({ level: 1 }).run()"
          :color="editor.isActive('heading', { level: 1 }) ? 'primary' : 'gray'"
          icon
        >
          <v-icon>mdi-format-header-1</v-icon>
        </v-btn>

        <v-btn
          @click="editor.chain().focus().toggleHeading({ level: 2 }).run()"
          :color="editor.isActive('heading', { level: 2 }) ? 'primary' : 'gray'"
          icon
        >
          <v-icon>mdi-format-header-2</v-icon>
        </v-btn>

        <v-btn
          @click="editor.chain().focus().toggleHeading({ level: 3 }).run()"
          :color="editor.isActive('heading', { level: 3 }) ? 'primary' : 'gray'"
          icon
        >
          <v-icon>mdi-format-header-3</v-icon>
        </v-btn>

        <v-btn
          @click="editor.chain().focus().toggleHeading({ level: 4 }).run()"
          :color="editor.isActive('heading', { level: 4 }) ? 'primary' : 'gray'"
          icon
        >
          <v-icon>mdi-format-header-4</v-icon>
        </v-btn>

        <v-btn
          @click="editor.chain().focus().toggleHeading({ level: 5 }).run()"
          :color="editor.isActive('heading', { level: 5 }) ? 'primary' : 'gray'"
          icon
        >
          <v-icon>mdi-format-header-5</v-icon>
        </v-btn>

        <v-btn
          @click="editor.chain().focus().toggleHeading({ level: 6 }).run()"
          :color="editor.isActive('heading', { level: 6 }) ? 'primary' : 'gray'"
          icon
        >
          <v-icon>mdi-format-header-6</v-icon>
        </v-btn>

        <v-btn
          @click="editor.chain().focus().toggleBulletList().run()"
          :color="editor.isActive('bulletList') ? 'primary' : 'gray'"
          icon
        >
          <v-icon>mdi-format-list-bulleted</v-icon>
        </v-btn>

        <v-btn
          @click="editor.chain().focus().toggleOrderedList().run()"
          :color="editor.isActive('orderedList') ? 'primary' : 'gray'"
          icon
        >
          <v-icon>mdi-format-list-numbered</v-icon>
        </v-btn>

        <v-btn
          @click="editor.chain().focus().undo().run()"
          icon
          :disabled="!editor.can().chain().focus().undo().run()"
        >
          <v-icon>mdi-undo</v-icon>
        </v-btn>

        <v-btn
          @click="editor.chain().focus().redo().run()"
          icon
          :disabled="!editor.can().chain().focus().redo().run()"
        >
          <v-icon>mdi-redo</v-icon>
        </v-btn>
      </div>
      <editor-content :editor="editor" />
    </div>
    <template v-slot:message="error">
      <div class="px-3">{{ error.message }}</div>
    </template>
  </v-input>
</template>

<script>
import StarterKit from "@tiptap/starter-kit";
import { Editor, EditorContent } from "@tiptap/vue-2";

export default {
  props: {
    value: [String, null],
    label: String,
    required: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  components: {
    EditorContent,
  },
  watch: {
    disabled: {
      handler(val) {
        this.editor.setEditable(!val);
      },
    },
  },
  data() {
    return {
      editor: null,
    };
  },
  mounted() {
    this.editor = new Editor({
      content: this.value,
      extensions: [StarterKit],
      onUpdate: () => {
        this.$emit("input", this.editor.getHTML());
      },
      editable: !this.disabled,
    });
  },
  beforeUnmount() {
    this.editor.destroy();
  },
};
</script>

<style lang="scss">
/* Basic editor styles */
.ProseMirror {
  padding: 12px;
  &:focus-visible {
    outline: none !important;
  }

  p {
    margin-bottom: 0px;
  }

  ul,
  ol {
    padding: 0 1rem;
  }

  h1,
  h2,
  h3,
  h4,
  h5,
  h6 {
    line-height: 1.1;
  }
}
</style>
